'use client';

import { BusyContext } from '@/components/common';
import { getRoadmapsTypeStatusesByVersion } from '@/domain/roadmap/api';
import { useRoadmaps, useRoadmapsVersions } from '@/domain/roadmap/hooks';
import { type PlatformVersionDTO, type RoadMapUpdate } from '@/domain/roadmap/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { useAuth } from '@/hooks/useAuth';
import { Divider } from '@andritzot/metris-web-ui/data-display/divider';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Grid } from '@andritzot/metris-web-ui/layout/grid';
import dayjs from 'dayjs';
import dayjsBusinessTime from 'dayjs-business-time';
import { forwardRef, type Ref, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

import {
  AffectedVersionsField,
  CategoryField,
  ClassificationField,
  CustomersField,
  DescriptionField,
  DetailedDescriptionField,
  FollowersField,
  type FollowersFieldHandle,
  ItemField,
  type JiraAutomationHandle,
  JiraAutomationSection,
  KeyField,
  OwnerField,
  ParticipantsField,
  PortfolioField,
  PriorityField,
  ProductField,
  QualityCertificateField,
  ReporterField,
  SpecificationField,
  StatusField,
  TypeField,
  VersionField,
} from './OverviewFields';
import { CreatedByField } from './OverviewFields/CreatedByField';
import { ExternalOriginField } from './OverviewFields/ExternalOriginField';
import { FeatureRequestField } from './OverviewFields/FeatureRequestField';
import { PlannedHoursField } from './OverviewFields/PlannedHoursField';

dayjs.extend(dayjsBusinessTime);

const METRIS_X_BLOCK_TYPES = new Set([5, 1012]);

export interface OverviewFormHandle {
  getValidatedData: () => Promise<RoadMapUpdate | null>;
  saveFollowers: () => Promise<void>;
  createJiraEpicIfNeeded: (item: string, description: string, roadmapId: number) => Promise<string | undefined>;
  resetForm: (data: RoadMapUpdate) => void;
  cancel: () => void;
  isDirty: () => boolean;
}

export interface OverviewRoadmapTabContentProps {
  ref?: Ref<OverviewFormHandle>;
  roadmap: RoadMapUpdate | undefined;
  isLoading: boolean;
  viewOnly: boolean;
}

interface OverviewFormContentProps {
  roadmap: RoadMapUpdate;
  viewOnly: boolean;
}

const OverviewFormContent = forwardRef<OverviewFormHandle, OverviewFormContentProps>(function OverviewFormContent(
  { roadmap, viewOnly },
  ref,
) {
  const storeInitRef = useRef(false);
  if (!storeInitRef.current) {
    storeInitRef.current = true;
    useRoadmapEditStore.setState({ editableRoadMap: roadmap });
  }

  const followersRef = useRef<FollowersFieldHandle>(null);
  const jiraRef = useRef<JiraAutomationHandle>(null);

  const { user } = useAuth();
  const { data: roadmaps } = useRoadmaps();
  const { data: versions } = useRoadmapsVersions(!!user);

  const editableRoadMap = useRoadmapEditStore((s) => s.editableRoadMap);

  const { control, reset, setValue, setFocus, setError, formState, trigger, getValues } = useForm<RoadMapUpdate>({
    defaultValues: roadmap,
  });

  const isInternal = useWatch({ control, name: 'origin' }) !== 1;
  const watchedProduct = useWatch({ control, name: 'product' });

  const totalResourceHours = useMemo(
    () =>
      editableRoadMap.type?.roadmapStatus?.flatMap((s) => s.resources ?? []).reduce((sum, r) => sum + r.hours, 0) ??
      null,
    [editableRoadMap.type?.roadmapStatus],
  );

  const isCustomerCare = useMemo(
    () => editableRoadMap.type?.customerCare ?? false,
    [editableRoadMap.type?.customerCare],
  );

  const isTypeMetrisXBlock = useMemo(
    () => METRIS_X_BLOCK_TYPES.has(editableRoadMap.type?.idRoadmapType ?? -1),
    [editableRoadMap.type?.idRoadmapType],
  );

  const computedRoadmapVersions = useMemo(
    () => versions?.filter((v) => v.release?.idPlatform === watchedProduct?.idPlatform) ?? [],
    [watchedProduct?.idPlatform, versions],
  );

  const [roadmapVersions, setRoadmapVersions] = useState<PlatformVersionDTO[]>(computedRoadmapVersions);
  const [prevComputedVersions, setPrevComputedVersions] = useState(computedRoadmapVersions);
  if (computedRoadmapVersions !== prevComputedVersions) {
    setPrevComputedVersions(computedRoadmapVersions);
    setRoadmapVersions(computedRoadmapVersions);
  }

  const updateStatusesByVersion = useCallback(
    async (typeId: number, version: PlatformVersionDTO) => {
      const roadmapStatus = await getRoadmapsTypeStatusesByVersion(typeId, version.idPlatformVersion);
      let lastStartDate = '';
      const statuses = [...(editableRoadMap.type?.roadmapStatus ?? [])]
        .sort((a, b) => b.statusOrder - a.statusOrder)
        .map((s) => {
          const status = roadmapStatus.find((rs) => rs.idRoadmapStatus === s.idRoadmapStatus);
          if (!status) return s;
          if (!lastStartDate) {
            lastStartDate = status.startDate ?? new Date().toISOString();
          }
          let newEndDate = '';
          if (s.isRelease) {
            lastStartDate = status.startDate ?? new Date().toISOString();
            newEndDate = dayjs(status.startDate).addBusinessDays(s.workingDays).toISOString();
          } else {
            newEndDate = dayjs(lastStartDate).subtractBusinessDays(1).toISOString();
            lastStartDate = dayjs(newEndDate)
              .subtractBusinessDays(Math.max(s.workingDays - 1, 1))
              .toISOString();
          }
          return {
            ...s,
            startDate: lastStartDate,
            newStartDate: lastStartDate,
            endDate: newEndDate,
            newEndDate,
            edited: true,
          };
        });

      useRoadmapEditStore.setState({
        editableRoadMap: {
          ...editableRoadMap,
          version,
          type: editableRoadMap.type
            ? { ...editableRoadMap.type, roadmapStatus: [...statuses].sort((a, b) => a.statusOrder - b.statusOrder) }
            : null,
        },
      });
    },
    [editableRoadMap],
  );

  const validateData = useCallback(
    (data: RoadMapUpdate) => {
      if (!data.item) {
        setFocus('item');
        setError('item', { message: 'Required' });
        throw new Error('Item is required');
      }
      if (!data.product) {
        setFocus('product');
        setError('product', { message: 'Required' });
        throw new Error('Product is required');
      }
      if (!data.description) {
        setFocus('description');
        setError('description', { message: 'Required' });
        throw new Error('Description is required');
      }
      if (
        roadmaps?.some(
          (r) =>
            r.item === data.item && r.idRoadmap !== data.idRoadmap && r.product.idProduct === data.product?.idProduct,
        )
      ) {
        setFocus('item');
        setError('item', { message: 'Roadmap item name already exists' });
        throw new Error('Roadmap item name already exists');
      }
    },
    [roadmaps, setError, setFocus],
  );

  const buildFormItem = useCallback(
    (formData: RoadMapUpdate): RoadMapUpdate => ({
      ...formData,
      item: formData.item.trim(),
      idProduct: formData.product?.idProduct ?? 0,
      type: formData.type ? { ...formData.type, roadmapStatus: editableRoadMap.type?.roadmapStatus ?? [] } : null,
      status: editableRoadMap.status,
      okrs: editableRoadMap.okrs,
      unitsImpact: editableRoadMap.unitsImpact,
      linkedItems: editableRoadMap.linkedItems,
      fundingSources: editableRoadMap.fundingSources,
      plannedHours: editableRoadMap.plannedHours,
    }),
    [editableRoadMap],
  );

  const getValidatedData = useCallback(async (): Promise<RoadMapUpdate | null> => {
    const isValid = await trigger();
    if (!isValid) return null;
    const formData = getValues();
    try {
      validateData(formData);
    } catch (e) {
      toast.error(`${e}`);
      return null;
    }
    const item = buildFormItem(formData);
    return item.idRoadmap ? item : null;
  }, [buildFormItem, getValues, trigger, validateData]);

  useImperativeHandle(
    ref,
    () => ({
      getValidatedData,
      saveFollowers: () => followersRef.current?.save() ?? Promise.resolve(),
      createJiraEpicIfNeeded: (item, description, roadmapId) =>
        jiraRef.current?.createEpicIfCloneMode(item, description, roadmapId) ?? Promise.resolve(undefined),
      resetForm: (data) => reset(data),
      cancel: () => {
        reset(roadmap);
        followersRef.current?.reset();
        useRoadmapEditStore.setState({ editableRoadMap: roadmap, isStoreDirty: false });
      },
      isDirty: () => formState.isDirty || (followersRef.current?.isDirty() ?? false),
    }),
    [followersRef, formState.isDirty, getValidatedData, jiraRef, reset, roadmap],
  );

  return (
    <form className="p-fluid">
      <Grid container spacing={4}>
        <Grid size={6}>
          <CreatedByField editableRoadMap={editableRoadMap} />
        </Grid>
        <Grid size={6}>
          <StatusField />
        </Grid>
        {editableRoadMap.idRoadmap > 0 && (
          <Grid size={6}>
            <TypeField
              viewOnly={viewOnly}
              control={control}
              updateStatusesByVersion={updateStatusesByVersion}
              setValue={setValue}
            />
          </Grid>
        )}
        <Grid size={3}>
          <CategoryField viewOnly={viewOnly} control={control} />
        </Grid>
        <Grid size={3}>
          <ProductField
            viewOnly={viewOnly}
            control={control}
            setValue={setValue}
            setRoadmapVersions={setRoadmapVersions}
          />
        </Grid>
        <Grid size={12} sx={{ mb: 1 }}>
          <PortfolioField viewOnly={viewOnly} control={control} isCustomerCare={isCustomerCare} />
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={6}>
          <ItemField viewOnly={viewOnly} control={control} />
        </Grid>
        {!!editableRoadMap.idRoadmap && (
          <Grid size={3}>
            <KeyField roadmap={editableRoadMap} />
          </Grid>
        )}
        <Grid size={3}>
          <VersionField
            viewOnly={viewOnly}
            isCustomerCare={isCustomerCare}
            control={control}
            product={watchedProduct}
            roadmapVersions={roadmapVersions}
            updateStatusesByVersion={updateStatusesByVersion}
          />
        </Grid>
        <Grid size={12}>
          <Divider />
        </Grid>
        <Grid size={12}>
          <Grid container spacing={4}>
            <Grid size={3}>
              <FeatureRequestField viewOnly={viewOnly} control={control} setValue={setValue} />
            </Grid>
            <Grid size={3}>
              <ExternalOriginField viewOnly={viewOnly} control={control} required={!isInternal} />
            </Grid>
            {!isCustomerCare && (
              <Grid size={isTypeMetrisXBlock ? 3 : 6}>
                <SpecificationField viewOnly={viewOnly} control={control} />
              </Grid>
            )}
            {isTypeMetrisXBlock && (
              <Grid size={3}>
                <QualityCertificateField viewOnly={viewOnly} control={control} />
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid size={3}>
          <PriorityField viewOnly={viewOnly} control={control} />
        </Grid>
        <Grid size={3}>
          <PlannedHoursField value={totalResourceHours} />
        </Grid>
        <Grid size={6}>
          <OwnerField viewOnly={viewOnly} control={control} />
        </Grid>
        {isCustomerCare && (
          <Grid size={12}>
            <ClassificationField viewOnly={viewOnly} control={control} />
          </Grid>
        )}
        {isCustomerCare && (
          <Grid size={12}>
            <AffectedVersionsField
              viewOnly={viewOnly}
              control={control}
              product={watchedProduct}
              roadmapVersions={roadmapVersions}
              required={!isInternal && roadmapVersions.length > 0}
            />
          </Grid>
        )}
        {isCustomerCare && (
          <>
            <Grid size={6}>
              <CustomersField viewOnly={viewOnly} control={control} required={!isInternal} />
            </Grid>
            <Grid size={6}>
              <ReporterField viewOnly={viewOnly} control={control} />
            </Grid>
          </>
        )}
        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={12}>
          <ParticipantsField viewOnly={viewOnly} control={control} />
        </Grid>
        {!!editableRoadMap.idRoadmap && (
          <Grid size={12}>
            <FollowersField ref={followersRef} roadmapId={editableRoadMap.idRoadmap} viewOnly={viewOnly} />
          </Grid>
        )}
        {!!editableRoadMap.idRoadmap && (
          <Grid size={12}>
            <JiraAutomationSection ref={jiraRef} control={control} viewOnly={viewOnly} />
          </Grid>
        )}
        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={12}>
          <DescriptionField viewOnly={viewOnly} control={control} />
        </Grid>
        {editableRoadMap.detailedDescription && (
          <Grid size={12}>
            <DetailedDescriptionField viewOnly={viewOnly} control={control} />
          </Grid>
        )}
      </Grid>
    </form>
  );
});

export function OverviewRoadmapTabContent({
  ref,
  roadmap,
  isLoading,
  viewOnly,
}: Readonly<OverviewRoadmapTabContentProps>) {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
        <BusyContext isLoading={true} />
      </Box>
    );
  }

  if (!roadmap) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Select a roadmap item to view its details.
      </Typography>
    );
  }

  return <OverviewFormContent key={roadmap.idRoadmap} roadmap={roadmap} viewOnly={viewOnly} ref={ref} />;
}
